import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useSchedulerService from '../../../app/services/hooks/useSchedulerService';
import PageHeader from '../../../components/PageHeader/PageHeader';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';

interface ScheduledJob {
    name: string;
    description: string;
    hours: number[];
    enabled: boolean;
    run_requested: boolean;
    last_run_at: string | null;
    last_status: '' | 'ok' | 'error' | 'running';
    last_message: string;
    last_duration_ms: number | null;
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
    ok: { label: 'OK', className: 'bg-success' },
    error: { label: 'Error', className: 'bg-danger' },
    running: { label: 'En curso', className: 'bg-info' }
};

const formatDateTime = (value: string | null) =>
    value
        ? new Date(value).toLocaleString('es-CL', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        })
        : '—';

// Nombre para mostrar de cada tarea (el nombre interno queda en api/scheduler/jobs.py)
const JOB_LABELS: Record<string, string> = {
    indicadores: 'Indicadores económicos',
    inapi_estados: 'Estados en INAPI',
    diario_oficial: 'Seguimiento en el Diario Oficial',
    estudios_cerrados: 'Estudios dados de baja',
    limpiar_intentos: 'Limpieza de intentos de acceso',
};

const formatHours = (hours: number[]) =>
    hours.length ? hours.map((hour) => `${String(hour).padStart(2, '0')}:00`).join(' · ') : '—';

const formatDuration = (ms: number | null) => {
    if (ms === null || ms === undefined) return '—';
    if (ms < 1000) return `${ms} ms`;
    const seconds = ms / 1000;
    return seconds < 60 ? `${seconds.toFixed(1)} s` : `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} s`;
};

/**
 * Programador de tareas (solo equipo Monere). Las tareas corren dentro del
 * contenedor; "Ejecutar ahora" solo deja la tarea solicitada y el proceso la
 * toma dentro del próximo minuto.
 */
const ScheduledJobs: React.FC = () => {
    const { getScheduledJobs, runScheduledJob, fetchingJobs } = useSchedulerService();
    const [jobs, setJobs] = useState<ScheduledJob[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [running, setRunning] = useState<string | null>(null);

    const load = () => {
        getScheduledJobs({
            onSuccess: (response) => {
                setJobs(response.data.jobs);
                setLoadError(null);
            },
            onError: (response) => setLoadError(response.message || 'No se pudieron cargar las tareas.')
        });
    };

    useEffect(() => {
        load();
    }, []);

    // Mientras haya tareas solicitadas o en curso, se refresca cada 15 segundos
    const pending = jobs.some((job) => job.run_requested || job.last_status === 'running');
    useEffect(() => {
        if (!pending) return;
        const timer = setInterval(load, 15000);
        return () => clearInterval(timer);
    }, [pending]);

    const handleRun = (job: ScheduledJob) => {
        setRunning(job.name);
        runScheduledJob(job.name, {
            onSuccess: (response) => {
                setRunning(null);
                toast.success(response.message);
                load();
            },
            onError: (response) => {
                setRunning(null);
                toast.error(response.message);
            }
        });
    };

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Estudios y Abogados', path: '/intranet/organizations' },
                    { label: 'Programador de tareas', isCurrent: true }
                ]}
                title="Programador de tareas"
                description="Tareas periódicas de Monere (hora de Chile). Ejecutar ahora la deja solicitada y se corre dentro del próximo minuto."
            />

            <DefaultCard>
                <div className="d-flex justify-content-end mb-3">
                    <ButtonGeneral onClick={load} title="Actualizar" icon="fa-sync" size="small" variant="outline" loading={fetchingJobs} />
                </div>

                {loadError ? (
                    <div className="text-center py-5">
                        <i className="fas fa-lock fa-3x text-secondary mb-3" />
                        <h5>{loadError}</h5>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover align-middle" data-testid="scheduled-jobs-table">
                            <thead>
                                <tr>
                                    <th>Tarea</th>
                                    <th>Horario</th>
                                    <th>Última ejecución</th>
                                    <th>Estado</th>
                                    <th>Mensaje</th>
                                    <th className="text-end">Duración</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {fetchingJobs && jobs.length === 0 ? (
                                    <tr><td colSpan={7} className="text-center py-4"><div className="spinner-border text-primary" /></td></tr>
                                ) : jobs.length === 0 ? (
                                    <tr><td colSpan={7} className="text-center text-muted py-4">No hay tareas registradas.</td></tr>
                                ) : jobs.map((job) => {
                                    const badge = STATUS_BADGE[job.last_status];
                                    return (
                                        <tr key={job.name}>
                                            <td>
                                                <span className="fw-bold d-block">
                                                    {JOB_LABELS[job.name] || job.name}
                                                    {!job.enabled && <span className="badge bg-secondary ms-2">Desactivada</span>}
                                                </span>
                                                <small className="text-muted">{job.description}</small>
                                            </td>
                                            <td><small>{formatHours(job.hours)}</small></td>
                                            <td><small>{formatDateTime(job.last_run_at)}</small></td>
                                            <td>
                                                {badge ? <span className={`badge ${badge.className}`}>{badge.label}</span> : <small className="text-muted">Sin ejecuciones</small>}
                                                {job.run_requested && <small className="d-block text-warning">Solicitada</small>}
                                            </td>
                                            <td style={{ maxWidth: 360 }}>
                                                <small className={job.last_status === 'error' ? 'text-danger' : 'text-muted'} style={{ wordBreak: 'break-word' }}>
                                                    {job.last_message || '—'}
                                                </small>
                                            </td>
                                            <td className="text-end"><small>{formatDuration(job.last_duration_ms)}</small></td>
                                            <td className="text-end">
                                                <ButtonGeneral
                                                    onClick={() => handleRun(job)}
                                                    title={job.run_requested ? 'Solicitada' : 'Ejecutar ahora'}
                                                    icon="fa-play"
                                                    size="small"
                                                    loading={running === job.name}
                                                    disabled={job.run_requested || job.last_status === 'running'}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </DefaultCard>
        </div>
    );
};

export default ScheduledJobs;
